import { ticketsApi, api, getAuthCredentials } from './client'

const BASE = '/api/v1/tickets/settings/portal'
const UPLOAD_BASE = '/api/v1/tickets/settings/portal'

// ── Types ─────────────────────────────────────────────────────────────────────

export type PortalTheme = 'enterprise' | 'gradient' | 'minimal'
export type HeroStyle   = 'gradient' | 'mesh' | 'solid' | 'image'

export const PORTAL_THEMES: Record<PortalTheme, { label: string; description: string }> = {
  enterprise: { label: 'Enterprise',  description: 'Bold branded hero with logo watermark and portal cards — inspired by Rakuten Cloud Help Center' },
  gradient:   { label: 'Gradient',    description: 'Smooth colour gradient header with clean form layout — inspired by CIQ Support portal' },
  minimal:    { label: 'Minimal',     description: 'Light, professional design with subtle accents and a focus on content' },
}

export type PortalFieldType =
  | 'text' | 'textarea' | 'select' | 'multiselect'
  | 'checkbox' | 'file' | 'priority' | 'email' | 'phone' | 'date' | 'number' | 'url'

export interface PortalField {
  id:           string
  label:        string
  type:         PortalFieldType
  required:     boolean
  placeholder?: string
  options?:     string[]
  helpText?:    string
  order:        number
}

export type TemplateColor = 'blue' | 'violet' | 'green' | 'amber' | 'red' | 'teal' | 'slate'

export interface PortalTemplate {
  id:          string
  name:        string
  description: string
  icon:        string           // lucide icon name or emoji
  color:       TemplateColor
  fields:      PortalField[]
  order:       number
  enabled:     boolean
  category?:   string           // org category (from ticketService.getCategories()) tickets from this template are filed under
}

export interface PortalBranding {
  logoUrl?:          string
  faviconUrl?:       string
  companyName:       string
  primaryColor:      string     // hex
  accentColor:       string     // hex
  heroStyle:         HeroStyle
  heroImageUrl?:     string
  welcomeTitle:      string
  welcomeSubtitle:   string
  searchPlaceholder: string
  footerText?:       string
}

export interface PortalSettings {
  enabled:              boolean
  theme:                PortalTheme
  branding:             PortalBranding
  requireLogin:         boolean
  showOrgRequests:      boolean
  allowAttachments:     boolean
  maxAttachmentSizeMb:  number
  templates:            PortalTemplate[]
  customCss?:           string
  slug?:                string
  // Chat is off by default — org admin opts into each mode independently.
  // Mode 1: chat with any available agent before raising a ticket.
  chatBeforeTicketEnabled: boolean
  // Mode 2: chat with the specific agent assigned to an existing ticket.
  chatWithAgentEnabled:    boolean
}

// ── Default templates shipped with every new portal ───────────────────────────

export const DEFAULT_TEMPLATES: PortalTemplate[] = [
  {
    id: 'general-support',
    name: 'General Support',
    description: 'Report an issue or ask a general question',
    icon: 'LifeBuoy',
    color: 'blue',
    order: 0,
    enabled: true,
    fields: [
      { id: 'subject',     label: 'Subject',      type: 'text',     required: true,  placeholder: 'Brief summary of your request', order: 0 },
      { id: 'description', label: 'Description',  type: 'textarea', required: true,  placeholder: 'Describe your issue in detail', order: 1 },
      { id: 'priority',    label: 'Priority',     type: 'priority', required: true,  order: 2 },
      { id: 'attachment',  label: 'Attachments',  type: 'file',     required: false, helpText: 'Attach screenshots or relevant files', order: 3 },
    ],
  },
  {
    id: 'bug-report',
    name: 'Bug Report',
    description: 'Report a software defect or unexpected behaviour',
    icon: 'Bug',
    color: 'red',
    order: 1,
    enabled: true,
    fields: [
      { id: 'subject',    label: 'Summary',             type: 'text',     required: true,  placeholder: 'Short description of the bug', order: 0 },
      { id: 'product',    label: 'Product / Component', type: 'text',     required: true,  placeholder: 'e.g. Dashboard, API, Mobile app', order: 1 },
      { id: 'severity',   label: 'Severity',            type: 'select',   required: true,  options: ['Critical', 'High', 'Medium', 'Low'], order: 2 },
      { id: 'steps',      label: 'Steps to Reproduce',  type: 'textarea', required: true,  placeholder: '1. Go to...\n2. Click on...\n3. See error', order: 3 },
      { id: 'expected',   label: 'Expected Behaviour',  type: 'textarea', required: false, order: 4 },
      { id: 'actual',     label: 'Actual Behaviour',    type: 'textarea', required: false, order: 5 },
      { id: 'attachment', label: 'Screenshots / Logs',  type: 'file',     required: false, order: 6 },
    ],
  },
  {
    id: 'feature-request',
    name: 'Feature Request',
    description: 'Suggest an improvement or new capability',
    icon: 'Lightbulb',
    color: 'amber',
    order: 2,
    enabled: true,
    fields: [
      { id: 'subject',  label: 'Feature Title',     type: 'text',     required: true,  placeholder: 'What do you want us to build?', order: 0 },
      { id: 'problem',  label: 'Problem to Solve',  type: 'textarea', required: true,  placeholder: 'What problem does this address?', order: 1 },
      { id: 'solution', label: 'Proposed Solution', type: 'textarea', required: false, placeholder: 'How do you envision it working?', order: 2 },
      { id: 'impact',   label: 'Business Impact',   type: 'select',   required: true,  options: ['Critical', 'High', 'Medium', 'Low'], order: 3 },
    ],
  },
  {
    id: 'billing-inquiry',
    name: 'Billing Inquiry',
    description: 'Questions about invoices, payments, or subscriptions',
    icon: 'CreditCard',
    color: 'green',
    order: 3,
    enabled: true,
    fields: [
      { id: 'subject',  label: 'Subject',         type: 'text',     required: true,  placeholder: 'Describe your billing question', order: 0 },
      { id: 'invoice',  label: 'Invoice Number',  type: 'text',     required: false, placeholder: 'e.g. INV-2024-001', order: 1 },
      { id: 'amount',   label: 'Amount in Question', type: 'text',  required: false, placeholder: 'e.g. $250.00', order: 2 },
      { id: 'type',     label: 'Query Type',      type: 'select',   required: true,  options: ['Invoice dispute', 'Refund request', 'Payment failure', 'Subscription change', 'Other'], order: 3 },
      { id: 'details',  label: 'Details',         type: 'textarea', required: true,  order: 4 },
    ],
  },
  {
    id: 'access-request',
    name: 'Access Request',
    description: 'Request access to a system, tool, or resource',
    icon: 'KeyRound',
    color: 'violet',
    order: 4,
    enabled: true,
    fields: [
      { id: 'subject',   label: 'Subject',          type: 'text',     required: true,  placeholder: 'What do you need access to?', order: 0 },
      { id: 'user',      label: 'User Email',        type: 'email',    required: true,  placeholder: 'Who needs access?', order: 1 },
      { id: 'resource',  label: 'System / Resource', type: 'text',     required: true,  placeholder: 'e.g. GitHub, AWS console, VPN', order: 2 },
      { id: 'level',     label: 'Access Level',      type: 'select',   required: true,  options: ['Read-only', 'Read-Write', 'Admin', 'Owner'], order: 3 },
      { id: 'reason',    label: 'Business Reason',   type: 'textarea', required: true,  order: 4 },
      { id: 'deadline',  label: 'Required By',       type: 'date',     required: false, order: 5 },
    ],
  },
]

const DEFAULT_SETTINGS: PortalSettings = {
  enabled: false,
  theme: 'gradient',
  branding: {
    companyName:       'Support Center',
    primaryColor:      '#3b82f6',
    accentColor:       '#1d4ed8',
    heroStyle:         'gradient',
    welcomeTitle:      'How can we help?',
    welcomeSubtitle:   'Submit a request or search our knowledge base',
    searchPlaceholder: 'Search for answers…',
    footerText:        'Powered by MultiAccess',
  },
  requireLogin:        true,
  showOrgRequests:     false,
  allowAttachments:    true,
  maxAttachmentSizeMb: 25,
  templates:           DEFAULT_TEMPLATES,
  chatBeforeTicketEnabled: false,
  chatWithAgentEnabled:    false,
}

// ── API ───────────────────────────────────────────────────────────────────────

function getToken(): string {
  try { return JSON.parse(localStorage.getItem('multiaccess-app') ?? '{}')?.state?.token ?? '' } catch { return '' }
}
const VITE_API_URL = import.meta.env.VITE_API_URL ?? ''

async function uploadPortalFile(endpoint: string, file: File): Promise<string> {
  const form = new FormData()
  form.append('file', file)
  const { orgId } = getAuthCredentials()
  const headers: Record<string, string> = { Authorization: `Bearer ${getToken()}` }
  if (orgId) {
    headers.Organizationid = orgId
    headers.organizationid = orgId
    headers['X-Org-ID'] = orgId
  }
  const res = await fetch(`${VITE_API_URL}${endpoint}`, {
    method: 'POST', body: form,
    headers,
  })
  if (!res.ok) throw new Error('Upload failed')
  const data = await res.json()
  return (data.url ?? data.storageUrl ?? '') as string
}

export const portalSettingsApi = {
  // Backend stores this as an opaque JSON blob per org and returns `{}` until
  // the org saves for the first time — merge onto defaults so a fresh org
  // doesn't crash the page on missing `branding`/`templates`.
  get:  async (): Promise<PortalSettings> => {
    let raw: Partial<PortalSettings> = {}
    try { raw = await ticketsApi.get<Partial<PortalSettings>>(BASE) ?? {} } catch { /* fall through to defaults */ }
    return {
      ...DEFAULT_SETTINGS,
      ...raw,
      branding: { ...DEFAULT_SETTINGS.branding, ...(raw.branding ?? {}) },
      templates: Array.isArray(raw.templates) ? raw.templates : DEFAULT_SETTINGS.templates,
    }
  },

  save: (p: PortalSettings): Promise<PortalSettings> =>
    ticketsApi.put<PortalSettings>(BASE, p),

  uploadLogo:    (file: File) => uploadPortalFile(`${UPLOAD_BASE}/logo`, file),
  uploadFavicon: (file: File) => uploadPortalFile(`${UPLOAD_BASE}/favicon`, file),

  // Templates
  saveTemplates: (templates: PortalTemplate[]) =>
    ticketsApi.put<PortalTemplate[]>(`${BASE}/templates`, { templates }),

  // Public portal (no auth) — reads branding + templates only
  getPublic: async (orgSlug: string): Promise<Pick<PortalSettings, 'branding' | 'theme' | 'templates' | 'allowAttachments' | 'showOrgRequests' | 'requireLogin'>> => {
    let raw: Partial<PortalSettings> = {}
    try { raw = await api.get<Partial<PortalSettings>>(`/portal/${orgSlug}/config`) ?? {} } catch { /* fall through to defaults */ }
    return {
      branding:         { ...DEFAULT_SETTINGS.branding, ...(raw.branding ?? {}) },
      theme:            raw.theme ?? DEFAULT_SETTINGS.theme,
      templates:        Array.isArray(raw.templates) ? raw.templates : DEFAULT_SETTINGS.templates,
      allowAttachments: raw.allowAttachments ?? DEFAULT_SETTINGS.allowAttachments,
      showOrgRequests:  raw.showOrgRequests ?? DEFAULT_SETTINGS.showOrgRequests,
      requireLogin:     raw.requireLogin ?? DEFAULT_SETTINGS.requireLogin,
    }
  },
}

export { DEFAULT_SETTINGS }
